/**
 * WhatsApp service caller + helpers.
 *
 * Talks to the Cloud Run service using a Firebase ID token. Used by:
 *   - Connect page (connect/disconnect)
 *   - SendWhatsAppButton (generic send)
 *   - WhatsAppTemplates / Logs / Settings pages
 */

import { auth, db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { buildDocumentPdfBase64 } from './whatsappDocumentBuilder';
import { TEMPLATE_BY_KEY, DEFAULT_TEMPLATES, renderTemplate } from './whatsappTemplates';

const SERVICE_URL = process.env.REACT_APP_WHATSAPP_SERVICE_URL || '';

// ── Service caller ──────────────────────────────────────────────
export async function callWhatsAppService(endpoint, method, body) {
  if (!SERVICE_URL) {
    throw new Error('WhatsApp service URL is not configured. Set REACT_APP_WHATSAPP_SERVICE_URL.');
  }
  const user = auth.currentUser;
  if (!user) throw new Error('Not logged in');
  const token = await user.getIdToken();
  const url = `${SERVICE_URL.replace(/\/$/, '')}/${endpoint}`;
  const init = { method, headers: { 'Authorization': `Bearer ${token}` } };
  if (method !== 'GET' && body) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  let data;
  try { data = await res.json(); } catch { data = { ok: res.ok }; }
  if (!res.ok) throw new Error(data?.error || `Service error (${res.status})`);
  return data;
}

// ── Phone normalization (PK default) ────────────────────────────
export function normalizePhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.startsWith('92')) return digits;
  if (digits.startsWith('0')) return '92' + digits.slice(1);
  return digits;
}

// ── Template lookup with company override support ───────────────
// Loads override from companies/{id}/whatsappTemplates/{key} if present,
// else falls back to DEFAULT_TEMPLATES entry. Returns merged template.
export async function loadTemplate(companyId, key) {
  const base = TEMPLATE_BY_KEY[key];
  if (!base) throw new Error(`Unknown template key: ${key}`);
  try {
    const snap = await getDoc(doc(db, 'companies', companyId, 'whatsappTemplates', key));
    if (snap.exists()) return { ...base, ...snap.data(), key };
  } catch (_) {}
  return base;
}

// Save (or upsert) a company override for a template.
export async function saveTemplate(companyId, key, overrides) {
  const ref = doc(db, 'companies', companyId, 'whatsappTemplates', key);
  await setDoc(ref, { ...overrides, key, updatedAt: new Date().toISOString() }, { merge: true });
}

export async function loadAllTemplates(companyId) {
  // Merge defaults with any saved overrides (one Firestore read per key — cheap).
  const merged = await Promise.all(DEFAULT_TEMPLATES.map(async t => {
    try {
      const snap = await getDoc(doc(db, 'companies', companyId, 'whatsappTemplates', t.key));
      return snap.exists() ? { ...t, ...snap.data(), key: t.key } : t;
    } catch (_) { return t; }
  }));
  return merged;
}

// ── Send the actual WhatsApp message ────────────────────────────
/**
 * Generic send wrapper used by SendWhatsAppButton.
 *
 * @param {object} params
 * @param {string} params.templateKey - e.g. 'saleInvoice', 'cashReceipt', 'customerStatement'
 * @param {object} params.doc - the document/transaction
 * @param {object} params.party - customer/vendor/staff master
 * @param {object} params.activeCompany
 * @param {object} [params.extras] - extra vars for template + PDF (rows, periodLabel, monthLabel, etc.)
 * @returns {Promise<{ok, messageId}>}
 */
export async function sendDocumentViaWhatsApp({ templateKey, doc, party, activeCompany, extras = {} }) {
  if (!activeCompany?.id) throw new Error('No active company');
  const rawPhone = party?.phone || party?.mobile;
  if (!rawPhone) throw new Error('Recipient has no phone number on file');
  const phone = normalizePhone(rawPhone);

  const template = await loadTemplate(activeCompany.id, templateKey);
  if (template.enabled === false) throw new Error('This template is disabled in WhatsApp Settings.');

  const currency = activeCompany.currencySymbol || 'Rs.';
  const fmt = (n) => `${currency} ${Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const vars = {
    customerName: party.companyName || party.contactPerson || party.name || '',
    vendorName: party.vendorName || party.contactPerson || party.name || '',
    staffName: party.staffName || party.fullName || party.name || '',
    companyName: activeCompany.companyName || '',
    docNumber: doc.docNumber || '',
    docDate: doc.docDate || extras.docDate || '',
    totalAmount: fmt(doc.totalAmount ?? doc.netPayable ?? extras.totalAmount ?? 0),
    balanceAmount: fmt(doc.balanceAmount ?? extras.balanceAmount ?? 0),
    dueDate: doc.dueDate || extras.dueDate || '',
    daysOverdue: extras.daysOverdue || '',
    month: extras.month || '',
    year: extras.year || '',
  };

  const message = renderTemplate(template.body, vars);

  let pdfBase64 = null;
  let pdfFilename = null;
  if (template.attachPdf) {
    pdfBase64 = await buildDocumentPdfBase64(template.docType || templateKey, doc, party, activeCompany, extras);
    const baseName = doc.docNumber || templateKey;
    pdfFilename = `${baseName}.pdf`;
  }

  const result = await callWhatsAppService('send', 'POST', {
    companyId: activeCompany.id,
    phone,
    message,
    pdfBase64,
    pdfFilename,
    relatedDoc: {
      tranTypeCode: template.tranType || doc.tranTypeCode || null,
      id: doc.id || null,
      docNumber: doc.docNumber || null
    }
  });
  return result;
}
